import type { FC } from 'react';
import React, { useCallback, useState } from 'react';

import ExpandableListItem from 'Components/shared/ExpandableListItem/ExpandableListItem';
import { useSeenFilter } from 'Components/VehicleList/SeenFilter';
import { triggerSeenFilterClickEvent } from 'Services/analytics/events';
import { useSeenFilterExperimentation } from 'Services/optimizely/experimentations/useSeenFilterExperimentation';
import type { Filter } from 'Types/filter';
import { type ViewingFilterValue, VIEWING_FILTER } from 'Utilities/seenVehicles/seenVehicles';

import { ChipSelect } from '../../ChipSelect';
import { MakeModelSection } from '../MakeModelSection';

import type {
	ChipFilterConfig,
	FilterSectionConfig,
	RadioFilterConfig,
	RangeFilterConfig,
} from './AllFiltersContent.consts';
import { FILTER_SECTIONS, VEHICLE_TYPE_SECTION } from './AllFiltersContent.consts';
import {
	applyChipFilter,
	applyRangeSection,
	getApplicableSections,
	getChipSelected,
	getInitialOpenSections,
	scrollSectionIntoView,
} from './AllFiltersContent.helpers';
import { ChipSection } from './ChipSection';
import { RadioSection } from './RadioSection';
import { RangeSection } from './RangeSection';
import { ToggleSection } from './ToggleSection';

import styles from './AllFiltersContent.module.scss';

interface AllFiltersContentProps {
	filters: Filter;
	onSeenFilterChange?: (value: ViewingFilterValue) => void;
	setValue: (arg: { id: string; value: unknown }) => void;
	unsetValue: (arg: { id: string }) => void;
}

export const AllFiltersContent: FC<AllFiltersContentProps> = ({
	filters,
	onSeenFilterChange,
	setValue,
	unsetValue,
}) => {
	const [seenFilter] = useSeenFilter();
	const isSeenFilterEnabled = useSeenFilterExperimentation();
	const [localSeenFilter, setLocalSeenFilter] = useState(seenFilter);

	const [openSections, setOpenSections] = useState<Set<string>>(() => {
		const sections = getInitialOpenSections(filters);
		if (isSeenFilterEnabled && localSeenFilter !== VIEWING_FILTER.ALL) {
			sections.add('viewed');
		}
		return sections;
	});

	const handleToggleSection = useCallback((id: string) => {
		setOpenSections((prev) => {
			const next = new Set(prev);
			const isOpening = !prev.has(id);
			if (isOpening) {
				next.add(id);
				scrollSectionIntoView(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	}, []);

	const handleRangeChange = useCallback(
		(config: RangeFilterConfig) => (from: Nullable<number>, to: Nullable<number>) =>
			applyRangeSection(config, from, to, setValue, unsetValue),
		[setValue, unsetValue],
	);

	const handleRadioChange = useCallback(
		(config: RadioFilterConfig) => (from: Nullable<number>, to: Nullable<number>) => {
			if (from === null) {
				unsetValue({ id: config.fromId });
			} else {
				setValue({ id: config.fromId, value: from });
			}
			if (to === null) {
				unsetValue({ id: config.toId });
			} else {
				setValue({ id: config.toId, value: to });
			}
		},
		[setValue, unsetValue],
	);

	const handleChipChange = useCallback(
		(config: ChipFilterConfig) => (selected: string[]) => applyChipFilter(config, selected, setValue),
		[setValue],
	);

	const handleSeenFilterToggle = useCallback(() => {
		const nextValue = localSeenFilter !== VIEWING_FILTER.NEW;
		const next = nextValue ? VIEWING_FILTER.NEW : VIEWING_FILTER.ALL;
		setLocalSeenFilter(next);
		onSeenFilterChange?.(next);
		triggerSeenFilterClickEvent(nextValue);
	}, [localSeenFilter, onSeenFilterChange]);

	const renderSectionContent = (section: FilterSectionConfig) => {
		const { config } = section;

		if (config.type === 'range') {
			return <RangeSection config={config} filters={filters} onChange={handleRangeChange(config)} />;
		}

		if (config.type === 'radio') {
			return <RadioSection config={config} filters={filters} onChange={handleRadioChange(config)} />;
		}

		if (config.type === 'toggle') {
			return <ToggleSection onChange={handleSeenFilterToggle} value={localSeenFilter} />;
		}

		return <ChipSection config={config} filters={filters} onSelect={handleChipChange(config)} />;
	};

	return (
		<>
			<div className={styles.vehicleTypeSection}>
				<span className={styles.fieldLabel}>{VEHICLE_TYPE_SECTION.title}</span>
				<ChipSelect
					onSelect={handleChipChange(VEHICLE_TYPE_SECTION.config)}
					options={VEHICLE_TYPE_SECTION.config.options}
					selected={getChipSelected(VEHICLE_TYPE_SECTION.config, filters)}
				/>
			</div>
			<MakeModelSection setValue={setValue} />
			<ul className={styles.allFiltersAccordionContainer}>
				{getApplicableSections({ isSeenFilterEnabled, sections: FILTER_SECTIONS }).map((section) => (
					<li key={section.id} className={styles.expandable}>
						<ExpandableListItem
							classNames={{
								container: styles.itemContainer,
								expanded: '',
								header: styles.itemHeader,
							}}
							id={`all-filters-accordion-${section.id}`}
							isExpanded={openSections.has(section.id)}
							onClick={() => handleToggleSection(section.id)}
							title={<div className={styles.itemTitle}>{section.title}</div>}
						>
							<section aria-label={section.title} className={styles.itemInner}>
								{renderSectionContent(section)}
							</section>
						</ExpandableListItem>
					</li>
				))}
			</ul>
		</>
	);
};
